import { Link } from "react-router-dom";

import type { TagRef } from "@/lib/api";

/**
 * Low-saturation rectangular tag pill. The unifying tag visual used in
 * question rows, the doc header, and the tag pages.
 */
export function Tag({
  slug,
  name,
  size = "sm",
  asLink = true,
}: {
  slug: string;
  name?: string;
  size?: "xs" | "sm";
  /** When `false` renders a span instead of a link (use inside other
   *  links to avoid nested anchors). */
  asLink?: boolean;
}) {
  const padding = size === "xs" ? "px-1.5 py-px" : "px-2 py-0.5";
  const text = size === "xs" ? "text-xs-dense" : "text-sm-dense";
  const className = [
    "inline-flex items-center rounded",
    "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300",
    padding,
    text,
    "hover:bg-zinc-200 dark:hover:bg-zinc-700",
  ].join(" ");

  const label = (
    <span title={name || slug}>{slug}</span>
  );

  if (!asLink) {
    return <span className={className}>{label}</span>;
  }
  return (
    <Link to={`/tags/${encodeURIComponent(slug)}`} className={className}>
      {label}
    </Link>
  );
}

export function TagList({
  tags,
  size = "sm",
  asLinks = true,
}: {
  tags: TagRef[];
  size?: "xs" | "sm";
  asLinks?: boolean;
}) {
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t) => (
        <Tag key={t.slug} slug={t.slug} name={t.name} size={size} asLink={asLinks} />
      ))}
    </div>
  );
}
